import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ChoiceRow } from "@/components/ProfileInputs";
import { api, errorMessage } from "@/lib/api";

const REASONS = [
  { v: "wrong_answer", l: "Answer is wrong" },
  { v: "multiple_answers", l: "More than one answer is right" },
  { v: "unclear", l: "Question is unclear" },
  { v: "other", l: "Something else" },
];

export function ReportButton({ attemptId, index, reported = false, testid = "quiz-report-button" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(reported);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/quizzes/report", { attempt_id: attemptId, index, reason, note });
      toast.success("Thanks, we'll take a look");
      setSent(true);
      setOpen(false);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={testid}
        onClick={() => setOpen(true)}
        aria-label="Report this question"
        className={`h-11 px-3 -mr-3 rounded-full flex items-center gap-1.5 text-xs transition-colors ${sent ? "text-[#E50914]" : "text-[#71717A] hover:text-white"}`}
      >
        <Flag size={14} className={sent ? "fill-[#E50914]" : ""} /> {sent ? "Reported" : "Report"}
      </button>
      <Sheet open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <SheetContent side="bottom" className="bg-[#0A0A0A] border-[#27272A] max-w-md mx-auto rounded-t-3xl px-5 pb-8" data-testid="quiz-report-sheet">
          <SheetHeader className="text-left mb-5">
            <SheetTitle className="font-display text-2xl uppercase tracking-tight">Report question</SheetTitle>
            <SheetDescription className="text-[#A1A1AA]">Think something's off? Tell us and we'll review it.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4">
            <ChoiceRow testid="quiz-report-reason" options={REASONS} value={reason} onChange={setReason} className="grid grid-cols-1 gap-2 [&>button]:text-left" />
            <textarea
              data-testid="quiz-report-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              placeholder="Optional: what should it say?"
              className="w-full min-h-[90px] bg-[#111113] border border-[#27272A] focus:border-[#E50914] rounded-xl p-3 text-base text-white placeholder:text-[#52525B] outline-none resize-none"
            />
            <button data-testid="quiz-report-submit" onClick={submit} disabled={!reason || busy} className="btn-primary">
              {busy ? <Loader2 size={18} className="animate-spin" /> : "Send report"}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
